import { apiClient } from '../../services/apiClient';
import { waitForJobCompletion, type AsyncJob } from '../../services/asyncJobApi';
import type {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  ProductListResponse,
  ProductListParams,
  UploadProductsResponse,
  BulkUploadResult,
  ImageSaveDestination,
} from './types';

/**
 * 商品一覧を取得
 */
export async function getProducts(params: ProductListParams = {}): Promise<ProductListResponse> {
  const { data } = await apiClient.get<ProductListResponse>('/products', { params });
  return data;
}

/**
 * 商品詳細を取得
 */
export async function getProduct(productId: string): Promise<Product> {
  const { data } = await apiClient.get<{ product: Product }>(`/products/${productId}`);
  return data.product;
}

/**
 * 商品を作成
 */
export async function createProduct(product: CreateProductRequest): Promise<Product> {
  const { data } = await apiClient.post<{ product: Product }>('/products', product);
  return data.product;
}

/**
 * 商品を更新
 */
export async function updateProduct(productId: string, product: UpdateProductRequest): Promise<Product> {
  const { data } = await apiClient.put<{ product: Product }>(`/products/${productId}`, product);
  return data.product;
}

/**
 * 商品を削除
 */
export async function deleteProduct(productId: string): Promise<{ unlinked_count: number }> {
  const { data } = await apiClient.delete<{ message: string; unlinked_count: number }>(
    `/products/${productId}`,
  );
  return { unlinked_count: data?.unlinked_count ?? 0 };
}

export type DeleteImpact = {
  total: number;
  self_personal: number;
  self_shared: number;
  others_personal: number;
  others_shared: number;
};

/**
 * 商品削除の影響範囲を取得 (削除確認 UI 用)
 */
export async function getDeleteImpact(productId: string): Promise<DeleteImpact> {
  const { data } = await apiClient.get<DeleteImpact>(`/products/${productId}/delete-impact`);
  return data;
}

/**
 * 論理削除済の商品を復元
 */
export async function restoreProduct(productId: string): Promise<void> {
  await apiClient.post(`/products/${productId}/restore`);
}

/**
 * CSV/Excelファイルから商品を一括登録（非同期ジョブとして実行）
 */
export async function uploadProducts(file: File, imageSaveDestination: ImageSaveDestination = 'none'): Promise<UploadProductsResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('image_save_destination', imageSaveDestination);
  const { data } = await apiClient.post<UploadProductsResponse>('/products/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * 商品一括登録ジョブの完了を待機（ポーリング）
 */
export async function waitForUploadCompletion(
  jobId: string,
  options?: {
    onProgress?: (job: AsyncJob) => void;
  }
): Promise<BulkUploadResult> {
  const job = await waitForJobCompletion<BulkUploadResult>(jobId, {
    pollInterval: 2000,
    maxDuration: 10 * 60 * 1000, // 最大10分
    onProgress: options?.onProgress,
  });
  return job.result_data;
}

/**
 * 商品画像のアップロード結果。SVG のときは表示用 PNG の url に加えて原本 (SVG) のストレージパスが付く。
 * 枠に保存するときは url と original_path を同じ PUT で送る (docs/features/product-image-vector-original-plan.md §3.2)。
 */
export type UploadedProductImage = {
  url: string;
  original_path?: string;
  original_content_type?: string;
  width?: number;
  height?: number;
};

/**
 * 商品画像をアップロード
 */
export async function uploadProductImage(file: File): Promise<UploadedProductImage> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<UploadedProductImage>('/products/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** 案件画像の枠 (API 表記): メイン = 'main'、追加画像 = 0〜7 */
export type ProductImageSlotParam = 'main' | number;

/**
 * 案件画像のベクター原本 (SVG) のダウンロード用署名URL (attachment・短期)。原本の公開URLは存在しない
 */
export async function getProductImageOriginalDownload(
  productId: string,
  slot: ProductImageSlotParam,
): Promise<{ url: string; content_type: string; file_name: string }> {
  const { data } = await apiClient.get<{ url: string; content_type: string; file_name: string }>(
    `/products/${productId}/images/original`,
    { params: { slot } },
  );
  return data;
}

/**
 * SVG 素材を案件画像の枠に置く。原本とラスタ版をサーバーが案件側へ複製し、url と original_path を保存する
 * (計画 §3.5 後半)。ラスタ画像の素材はこの API ではなく URL を直接 PUT する
 */
export async function setProductImageFromMaterial(
  productId: string,
  body: { slot: ProductImageSlotParam; folder_type: 'temporary' | 'cloud'; material_id: string },
): Promise<Product> {
  const { data } = await apiClient.post<{ product: Product }>(`/products/${productId}/images/from-material`, body);
  return data.product;
}

/**
 * CSVテンプレートをダウンロード
 */
export async function downloadTemplate(): Promise<void> {
  const response = await apiClient.get('/products/template', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'products_template.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Excelテンプレートをダウンロード（セル書式設定済み）
 */
export async function downloadTemplateExcel(): Promise<void> {
  const response = await apiClient.get('/products/template/excel', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'products_template.xlsx';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
